import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  sendMagicLink,
  setPassphrase,
  unlockVaultWithDevice,
  unlockVaultWithPassphrase,
  getSession,
  signOut,
  onAuthStateChange,
  saveVault,
} from '../services/auth-service';
import { generateDeviceId } from '../utils/indexeddb-storage';
import logger from '../utils/logger';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [vault, setVault] = useState(null);
  const [vaultKey, setVaultKey] = useState(null); // CryptoKey instance for session
  const [loading, setLoading] = useState(true);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [deviceId] = useState(() => generateDeviceId());

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const { user: currentUser, session: currentSession } = await getSession();
        
        if (mounted) {
          setUser(currentUser);
          setSession(currentSession);

          // If user is authenticated, try to auto-unlock vault
          if (currentUser) {
            logger.info('AUTH', `User authenticated: ${currentUser.id.substring(0, 8)}...`);
            const unlocked = await tryAutoUnlock(currentUser.id);
            if (unlocked) {
              logger.info('VAULT', 'Vault auto-unlocked successfully on page load');
            } else {
              logger.warn('VAULT', 'Vault auto-unlock failed - user needs to enter passphrase');
            }
          } else {
            logger.info('AUTH', 'No authenticated user found');
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    // Listen to auth state changes (handles Magic Link callbacks automatically)
    const { data: { subscription } } = onAuthStateChange(async (user, event, session) => {
      if (mounted) {
        setUser(user);
        setSession(session);

        if (user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          // User signed in via Magic Link or session refreshed
          logger.info('AUTH', `Auth event: ${event} for user ${user.id.substring(0, 8)}...`);
          // Try auto-unlock after sign-in
          const unlocked = await tryAutoUnlock(user.id);
          if (unlocked) {
            logger.info('VAULT', 'Vault auto-unlocked after sign-in');
          } else {
            logger.warn('VAULT', 'Vault auto-unlock failed - user needs to enter passphrase');
            // If auto-unlock failed, user needs to enter passphrase
            // This will be handled by the modal
          }
        } else if (event === 'SIGNED_OUT') {
          logger.info('AUTH', 'User signed out - clearing vault');
          // Clear vault on sign-out
          setVault(null);
          setVaultKey(null);
          setIsUnlocked(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Try to auto-unlock vault using device key
   */
  const tryAutoUnlock = useCallback(async (userId) => {
    try {
      logger.debug('VAULT', `Attempting auto-unlock for user ${userId.substring(0, 8)}... with device ${deviceId.substring(0, 8)}...`);
      const result = await unlockVaultWithDevice(userId, deviceId);
      if (result.success && result.vault && result.vaultKey) {
        setVault(result.vault);
        setVaultKey(result.vaultKey);
        setIsUnlocked(true);
        logger.info('VAULT', `Auto-unlock successful. Wallets: ${result.vault.wallets?.length || 0}`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error in auto-unlock:', error);
      return false;
    }
  }, [deviceId]);

  /**
   * Send Magic Link to email
   */
  const handleSendMagicLink = useCallback(async (email) => {
    return await sendMagicLink(email);
  }, []);

  // Magic Link authentication is handled automatically by Supabase JS client
  // When user clicks magic link, they're redirected back and Supabase detects the session
  // We handle this in the auth state change listener below

  /**
   * Set passphrase for first time on device
   */
  const handleSetPassphrase = useCallback(async (passphrase) => {
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    const result = await setPassphrase(passphrase, user.id, deviceId);
    if (result.success) {
      // After setting passphrase, unlock the vault
      const unlockResult = await unlockVaultWithPassphrase(passphrase, user.id, deviceId);
      if (unlockResult.success) {
        setVault(unlockResult.vault);
        setVaultKey(unlockResult.vaultKey);
        setIsUnlocked(true);
      }
    }
    return result;
  }, [user, deviceId]);

  /**
   * Unlock vault with passphrase
   */
  const handleUnlockWithPassphrase = useCallback(async (passphrase) => {
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    const result = await unlockVaultWithPassphrase(passphrase, user.id, deviceId);
    if (result.success) {
      setVault(result.vault);
      setVaultKey(result.vaultKey);
      setIsUnlocked(true);
    }
    return result;
  }, [user, deviceId]);

  /**
   * Save vault data
   */
  const handleSaveVault = useCallback(async (vaultData) => {
    if (!user || !vaultKey) {
      return { success: false, error: 'Vault not unlocked' };
    }

    const result = await saveVault(vaultData, user.id, vaultKey);
    if (result.success) {
      setVault(vaultData);
    }
    return result;
  }, [user, vaultKey]);

  /**
   * Sign out
   */
  const handleSignOut = useCallback(async () => {
    const result = await signOut();
    if (result.success) {
      setUser(null);
      setSession(null);
      setVault(null);
      setVaultKey(null);
      setIsUnlocked(false);
    }
    return result;
  }, []);

  const value = {
    user,
    session,
    vault,
    vaultKey,
    loading,
    isUnlocked,
    deviceId,
    sendMagicLink: handleSendMagicLink,
    setPassphrase: handleSetPassphrase,
    unlockWithPassphrase: handleUnlockWithPassphrase,
    saveVault: handleSaveVault,
    signOut: handleSignOut,
    tryAutoUnlock: () => user && tryAutoUnlock(user.id),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
